import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { register as registerUser } from '../../features/auth/authSlice';
import CustomHeader from '../../components/layout/CustomHeader';
import './Register.css';

const Register = () => {
  const [formData, setFormData] = useState({
    fullname: '',
    email: '',
    countryCode: '+91',
    phone: '',
    password: '',
    confirmPassword: '',
    terms: false,
    role: 'student',
    adminSecret: ''
  });
  const [errors, setErrors] = useState({});
  const [showSuccess, setShowSuccess] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Set background image on component mount
  useEffect(() => {
    document.body.style.background = `linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.7)), url('/login.jpg')`;
    document.body.style.backgroundSize = 'cover';
    document.body.style.backgroundPosition = 'center';
    document.body.style.backgroundAttachment = 'fixed';
    document.body.style.overflow = 'auto';
    
    // Cleanup function to reset background when component unmounts
    return () => {
      document.body.style.background = '';
      document.body.style.backgroundSize = '';
      document.body.style.backgroundPosition = '';
      document.body.style.backgroundAttachment = '';
      document.body.style.overflow = '';
      // Remove any dashboard-body class that might have been added
      document.body.classList.remove('dashboard-body');
    };
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const isValidEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const isValidPhone = (phone) => {
    const re = /^\d{10}$/;  // Basic validation for 10 digits
    return re.test(phone);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Reset error messages
    setErrors({});
    
    const { fullname, email, countryCode, phone, password, confirmPassword, terms, role, adminSecret } = formData;
    let newErrors = {};
    
    // Basic validation
    if (!fullname) {
      newErrors.fullname = 'Full name is required';
    }
    
    if (!email) {
      newErrors.email = 'Email is required';
    } else if (!isValidEmail(email)) {
      newErrors.email = 'Please enter a valid email';
    }

    if (!phone) {
      newErrors.phone = 'Phone number is required';
    } else if (!isValidPhone(phone)) {
      newErrors.phone = 'Please enter a valid phone number';
    }
    
    if (!password) {
      newErrors.password = 'Password is required';
    } else if (password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }
    
    if (!confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (password !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    
    if (!terms) {
      alert('Please agree to the Terms & Conditions');
      return;
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    // Dispatch register action to backend
    dispatch(registerUser({
      name: fullname,
      email,
      password,
      role,
      adminSecret: role === 'admin' ? adminSecret : undefined
    }))
      .unwrap()
      .then(() => {
        setShowSuccess(true);
      })
      .catch((err) => {
        setErrors({ api: err });
      });
  };

  const goBack = () => {
    navigate(-1);
  };

  const goToLogin = () => {
    navigate('/login');
  };

  const goToHome = () => {
    navigate('/');
  };

  if (showSuccess) {
    return (
      <>
        <CustomHeader activePage="register" />
        
        <button className="back-button" onClick={goBack}>
          <i className="fas fa-arrow-left"></i>
        </button>
        
        <div className="register-container">
          <div className="register-header">
            <img src="/logo.PNG" alt="IntelUniVen Logo" />
            <h1>Registration Successful!</h1>
            <p>Your account has been created successfully!</p>
          </div>
          
          <div className="success-options">
            <button onClick={goToLogin} className="btn-primary">
              Login Now
            </button>
            <button onClick={goToHome} className="btn-secondary">
              Go to Home
            </button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <CustomHeader activePage="register" />
      
      <button className="back-button" onClick={goBack}>
        <i className="fas fa-arrow-left"></i>
      </button>
      
      <div className="register-container">
        <div className="register-header">
          <img src="/logo.PNG" alt="IntelUniVen Logo" />
          <h1>Create Account</h1>
          <p>Join our community today</p>
        </div>
        
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <i className="fas fa-user"></i>
            <input 
              type="text" 
              name="fullname"
              placeholder="Full Name" 
              value={formData.fullname}
              onChange={handleChange}
              required 
            />
            {errors.fullname && <div className="error-message">{errors.fullname}</div>}
          </div>
          
          <div className="form-group">
            <i className="fas fa-envelope"></i>
            <input 
              type="email" 
              name="email"
              placeholder="Email Address" 
              value={formData.email}
              onChange={handleChange}
              required 
            />
            {errors.email && <div className="error-message">{errors.email}</div>}
          </div>
          
          <div className="form-group">
            <i className="fas fa-phone"></i>
            <div className="phone-input-group">
              <select 
                name="countryCode"
                value={formData.countryCode}
                onChange={handleChange}
                required
              >
                <option value="+91">+91 (IN)</option>
                <option value="+1">+1 (US)</option>
                <option value="+44">+44 (UK)</option>
                <option value="+61">+61 (AU)</option>
                <option value="+86">+86 (CN)</option>
              </select>
              <input 
                type="tel" 
                name="phone"
                placeholder="Enter phone" 
                value={formData.phone}
                onChange={handleChange}
                required 
              />
            </div>
            {errors.phone && <div className="error-message">{errors.phone}</div>}
          </div>
          
          <div className="form-group">
            <i className="fas fa-lock"></i>
            <input 
              type="password" 
              name="password"
              placeholder="Password" 
              value={formData.password}
              onChange={handleChange}
              required 
            />
            {errors.password && <div className="error-message">{errors.password}</div>}
          </div>
          
          <div className="form-group">
            <i className="fas fa-lock"></i>
            <input 
              type="password" 
              name="confirmPassword"
              placeholder="Confirm Password" 
              value={formData.confirmPassword}
              onChange={handleChange}
              required 
            />
            {errors.confirmPassword && <div className="error-message">{errors.confirmPassword}</div>}
          </div>
          
          <div className="form-group">
            <label htmlFor="role">Role</label>
            <select
              name="role"
              value={formData.role}
              onChange={handleChange}
              className="form-input"
            >
              <option value="student">Student</option>
              <option value="instructor">Instructor</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          
          {formData.role === 'admin' && (
            <div className="form-group">
              <label htmlFor="adminSecret">Admin Secret</label>
              <input
                type="password"
                name="adminSecret"
                placeholder="Enter admin secret"
                value={formData.adminSecret}
                onChange={handleChange}
                className="form-input"
                required={formData.role === 'admin'}
              />
            </div>
          )}
          
          <div className="terms">
            <input 
              type="checkbox" 
              name="terms"
              checked={formData.terms}
              onChange={handleChange}
              required
            />
            <label htmlFor="terms">I agree to the <a href="#">Terms & Conditions</a></label>
          </div>
          
          <button type="submit" className="register-btn">Register</button>
          
          {errors.api && <div className="error-message">{errors.api}</div>}
          
          <div className="login-link">
            Already have an account? <Link to="/login">Login here</Link>
          </div>
        </form>
      </div>
    </>
  );
};

export default Register;